// src/features/workspaces/components/WorkspaceSwitcher.tsx
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient, trpc } from "@/utils/trpc";
import type { Workspace } from "@/utils/custom-types";
import { Route as WorkspaceRoute } from "@/routes/$workspaceId/route";
import { useWorkspaceContext } from "@/contexts/workspace-context";


export const WorkspaceSwitcher = ({
    currentWorkspace,
    onOpenWorkspaceDialog,
}: {
    currentWorkspace: Workspace;
    onOpenWorkspaceDialog?: () => void;
}) => {
    const [isOpen, setIsOpen] = useState(false);

	const { userWorkspaces } = useWorkspaceContext();

	const mutateLastWorkspaceId = useMutation(
		trpc.me.updateLastWorkspace.mutationOptions({
			onMutate: () => {},
		}),
	);

    const handleAddWorkspace = () => {
        onOpenWorkspaceDialog?.();
        setIsOpen(false);
    };

	const router = useRouter();

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<div
					className="group flex flex cursor-pointer items-center justify-center gap-2"
					onMouseEnter={() => setIsOpen(true)}
					onMouseLeave={() => setIsOpen(false)}
				>
					<div className="flex h-9 w-9 items-center justify-center rounded-sm bg-sidebar-accent text-xs uppercase" />
					<span className="font-bold">{currentWorkspace.name}</span>
				</div>
			</PopoverTrigger>

			<PopoverContent
				align="start"
				className="w-[300px]"
				onMouseEnter={() => setIsOpen(true)}
				onMouseLeave={() => setIsOpen(false)}
			>
				<div className="space-y-1 max-h-100 overflow-y-auto">
					{userWorkspaces.map((ws) => (
						<div
							key={ws.id}
							className="flex cursor-pointer items-center gap-2 rounded-md p-2 hover:bg-accent"
							onClick={() => {
								mutateLastWorkspaceId.mutate({ workspaceId: ws.id });
								// queryClient.invalidateQueries({
								// 	predicate: (query) => {
								// 		const protectedKey = ["session"];
									
								// 		return JSON.stringify(query.queryKey) !== JSON.stringify(protectedKey);
								// 	},
								// });
								router.navigate({ to: `/${ws.id}` });
								setIsOpen(false);
							}}
						>
							<div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs uppercase">
								{ws.name.charAt(0)}
							</div>
							<span>{ws.name}</span>
						</div>
					))}

					<div className="my-2 border-t" />

					<div
						className="flex cursor-pointer items-center gap-2 rounded-md p-2 text-primary hover:bg-accent"
						onClick={() => {
							handleAddWorkspace();
						}}
					>
						<Plus className="h-4 w-4" />
						<span>Add workspace</span>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
};
